import { useState, useEffect, useMemo } from "react";
import Title from "../shared/components/Title";
import ScorecardTable from "../features/customer-health/components/ScorecardTable";
import apiClient from "../utils/apiClient";

const HEALTH_ORDER = { red: 0, amber: 1, green: 2 };

function SummaryCard({ label, value, sub, accent }) {
  const accentMap = {
    red:    "border-red-300 bg-red-50",
    amber:  "border-amber-300 bg-amber-50",
    green:  "border-emerald-300 bg-emerald-50",
    default: "border-gray-200 bg-white",
  };
  const textMap = {
    red: "text-red-600", amber: "text-amber-600", green: "text-emerald-600", default: "text-gray-900",
  };
  return (
    <div className={`rounded-xl border shadow-sm px-5 py-4 ${accentMap[accent] || accentMap.default}`}>
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">{label}</p>
      <p className={`text-2xl font-bold ${textMap[accent] || textMap.default}`}>{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
    </div>
  );
}

export default function CustomerHealth() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeCsm, setActiveCsm] = useState(null);
  const [groupByCsm, setGroupByCsm] = useState(false);

  useEffect(() => {
    apiClient.get("/api/v1/customerHealth/scorecard")
      .then(r => r.json())
      .then(json => setData(json))
      .catch(() => setError("Failed to load scorecard. Check your VPN connection and try again."))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    if (!data?.companies) return [];
    const list = activeCsm
      ? data.companies.filter(c => c.csm === activeCsm)
      : data.companies;
    return [...list].sort(
      (a, b) => (HEALTH_ORDER[a.health] - HEALTH_ORDER[b.health]) || (b.revenue_4wk - a.revenue_4wk)
    );
  }, [data, activeCsm]);

  const summary = useMemo(() => {
    if (!filtered.length) return null;
    const red   = filtered.filter(c => c.health === "red").length;
    const amber = filtered.filter(c => c.health === "amber").length;
    const green = filtered.filter(c => c.health === "green").length;
    const withDifot = filtered.filter(c => c.difot_pct != null);
    const avgDifot = withDifot.length
      ? (withDifot.reduce((s, c) => s + c.difot_pct, 0) / withDifot.length).toFixed(1)
      : null;
    const totalOpen = filtered.reduce((s, c) => s + (c.open_tickets || 0), 0);
    return { red, amber, green, total: filtered.length, avgDifot, totalOpen };
  }, [filtered]);

  return (
    <div className="min-h-screen bg-[#f0f5fb] p-6">
      <div className="max-w-screen-2xl mx-auto space-y-5">

        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <Title>Customer Health Scorecard</Title>
            <p className="text-sm text-gray-500 mt-0.5">
              Operational health per customer — open tickets, DIFOT, and revenue at a glance
            </p>
          </div>
          {data && (
            <button
              onClick={() => setGroupByCsm(v => !v)}
              className={`text-xs px-3 py-1.5 rounded-lg border font-medium transition-colors ${
                groupByCsm
                  ? "bg-blue-600 text-white border-blue-600"
                  : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"
              }`}
            >
              {groupByCsm ? "Grouped by CSM" : "Group by CSM"}
            </button>
          )}
        </div>

        {/* Data period banner */}
        {data && (
          <div className="bg-white border border-gray-200 rounded-xl px-4 py-2.5 flex flex-wrap gap-x-6 gap-y-1 text-xs text-gray-500">
            <span><span className="font-semibold text-gray-700">Revenue & GP:</span> {data.revenue_period}</span>
            <span><span className="font-semibold text-gray-700">DIFOT:</span> {data.difot_period
              ? `${data.difot_period} (last available — feed gap flagged)`
              : "No data available"
            }</span>
            <span><span className="font-semibold text-gray-700">Tickets:</span> {data.tickets_period}</span>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-24">
            <div className="flex flex-col items-center gap-3">
              <div className="w-10 h-10 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
              <p className="text-sm text-gray-500">Loading scorecard data…</p>
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700">{error}</div>
        )}

        {/* Summary KPI cards */}
        {!loading && summary && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            <SummaryCard label="Total Customers" value={summary.total} />
            <SummaryCard label="At Risk" value={summary.red} sub="open tickets > 15" accent="red" />
            <SummaryCard label="Watch" value={summary.amber} sub="open tickets 6–15" accent="amber" />
            <SummaryCard label="Healthy" value={summary.green} accent="green" />
            <SummaryCard
              label="Open Tickets"
              value={summary.totalOpen}
              sub={`across ${filtered.length} customers`}
              accent={summary.totalOpen > 100 ? "amber" : "default"}
            />
          </div>
        )}

        {/* CSM filter pills */}
        {!loading && data?.csm_list?.length > 0 && (
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setActiveCsm(null)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                activeCsm === null
                  ? "bg-blue-600 text-white"
                  : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-50"
              }`}
            >
              All CSMs
            </button>
            {data.csm_list.map(csm => (
              <button
                key={csm}
                onClick={() => setActiveCsm(activeCsm === csm ? null : csm)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  activeCsm === csm
                    ? "bg-blue-600 text-white"
                    : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-50"
                }`}
              >
                {csm}
              </button>
            ))}
          </div>
        )}

        {/* Scorecard table */}
        {!loading && !error && (
          <ScorecardTable companies={filtered} groupByCsm={groupByCsm && !activeCsm} />
        )}

        {/* DIFOT footnote */}
        {!loading && data?.difot_period && (
          <p className="text-xs text-gray-400 px-1">
            * DIFOT data from {data.difot_period}. FCT_CONSIGNMENT feed appears to have stopped — flag to data team for investigation.
          </p>
        )}

      </div>
    </div>
  );
}
